// M's two sounds, both Alexander's own:
// - "done" (assets/m-sent.mp3, 1.49 s, 24 KB) — 1.5.9 with Sending's tick; 1.5.10 on every completion: a payment sent,
//   a request paid (the pay page), a note put in the wallet, a new wallet,
//   Add money, a Drop claimed or released, a swap. Not the welcome money (Alexander, 24 Oct). At a merchant's checkout (the
//   embed in the merchant's page) parked in 1.5.12 (Alexander, 25 Oct) and back in 1.5.37 from an <audio> element (below).
// - "arrive" (assets/m-arrive.mp3, 1.92 s, 32 KB) — 1.5.10, the arrival splash (splash.ts), the first login of the day.
//
// - Bandwidth: "done" is fetched once, on the first tap anywhere; "arrive" only when an arrival is due (the login tap
//   of the day's first login). Content-hashed names the browser caches for good (_headers: /assets/* immutable).
// - iOS plays sound only after a tap: every tap that finds the audio context missing or asleep wakes it
//   (installSoundUnlock, a capture listener at boot), so a sound can play seconds later when Arc confirms.
// - The ring/silent switch is respected: Safari's audio session is asked for "ambient" (silenced by the switch; mixes
//   with, rather than stops, other audio). Where the browser has no audio session, Web Audio's default applies.
// - Never in the way: every step is best-effort; a missing file, a refused context or a decode error means no sound.
import DONE_URL from "./assets/m-sent.mp3";
import ARRIVE_URL from "./assets/m-arrive.mp3";

export type SoundName = "done" | "arrive";
const FILES: Record<SoundName, { url: string; lead: number; gain: number }> = {
  /** Starts with 50 ms of silence (mostly the encoder's padding): skipped, so the swell meets the tick. Peaks at −0.4 dBFS: 70 %. */
  done: { url: DONE_URL, lead: 0.045, gain: 0.7 },
  /** Trimmed to its audible 1.92 s with a 60 ms fade; it starts at once. */
  arrive: { url: ARRIVE_URL, lead: 0, gain: 0.8 },
};

type AudioCtor = typeof AudioContext;
let ctx: AudioContext | null = null;
const bytes: Partial<Record<SoundName, Promise<ArrayBuffer | null>>> = {};
const buffers: Partial<Record<SoundName, Promise<AudioBuffer | null>>> = {};

/** Fetch a sound's file (once). */
export function preloadSound(name: SoundName = "done"): void {
  if (bytes[name]) return;
  bytes[name] = fetch(FILES[name].url).then((r) => (r.ok ? r.arrayBuffer() : null)).catch(() => null);
}

function decode(name: SoundName): Promise<AudioBuffer | null> | null {
  if (!ctx || !bytes[name]) return null;
  const c = ctx;
  return (buffers[name] ??= bytes[name]!.then((b) => (b ? c.decodeAudioData(b.slice(0)) : null)).catch(() => null));
}

/** Inside a tap (a user gesture): make or wake the audio context, and decode what is fetched. */
export function unlockSound(): void {
  try {
    const nav = navigator as Navigator & { audioSession?: { type: string } };
    if (nav.audioSession && nav.audioSession.type !== "ambient") nav.audioSession.type = "ambient";
    const Ctor = (window.AudioContext ?? (window as unknown as { webkitAudioContext?: AudioCtor }).webkitAudioContext) as AudioCtor | undefined;
    if (!Ctor) return;
    ctx ??= new Ctor();
    if (ctx.state !== "running") void ctx.resume().catch(() => {});
    preloadSound("done");
    for (const n of Object.keys(bytes) as SoundName[]) decode(n);
  } catch { /* no sound, no harm */ }
}

/** At boot: every tap that finds audio asleep wakes it (iOS needs the tap itself; a context also sleeps in the background). */
export function installSoundUnlock(): void {
  const wake = () => { if (!ctx || ctx.state !== "running" || !buffers.done) unlockSound(); };
  for (const e of ["pointerup", "touchend", "click", "keydown"] as const) window.addEventListener(e, wake, { capture: true, passive: true });
}

/**
 * Play a sound (if a tap has unlocked audio). Resolves true when it has started, false when it can't (no unlock, no
 * file, not decoded within `wait` ms) — the splash starts its line on that, so the line and the sound start together.
 */
export function playSound(name: SoundName, wait = 0): Promise<boolean> {
  window.dispatchEvent(new CustomEvent("m:sound", { detail: name })); // for the UI suites (and Lab, later)
  const c = ctx;
  if (!c) return Promise.resolve(false);
  if (name !== "done") preloadSound(name);
  const b = decode(name);
  if (!b) return Promise.resolve(false);
  const late = new Promise<null>((r) => setTimeout(() => r(null), wait > 0 ? wait : 4000));
  return Promise.race([b, wait > 0 ? late : b]).then((buf) => {
    if (!buf) return false;
    try {
      if (c.state !== "running") void c.resume().catch(() => {});
      const f = FILES[name], src = c.createBufferSource(), g = c.createGain();
      g.gain.value = f.gain;
      src.buffer = buf;
      src.connect(g).connect(c.destination);
      src.start(0, f.lead);
      playing.set(name, { src, g });
      src.onended = () => { if (playing.get(name)?.src === src) playing.delete(name); };
      return true;
    } catch { return false; }
  });
}

const playing = new Map<SoundName, { src: AudioBufferSourceNode; g: GainNode }>();
/** Fade a playing sound out (the splash's tap to skip). */
export function fadeSound(name: SoundName, ms = 150): void {
  const p = playing.get(name), c = ctx;
  if (!p || !c) return;
  try {
    const t = c.currentTime;
    p.g.gain.setValueAtTime(p.g.gain.value, t);
    p.g.gain.linearRampToValueAtTime(0, t + ms / 1000);
    p.src.stop(t + ms / 1000 + 0.02);
  } catch { /* gone already */ }
  playing.delete(name);
}

/** M's "done": a completion has landed. */
export function playDone(): void { void playSound("done"); }

// 1.5.37 (Alexander, 29 Oct: "revisit playing sound on the merchant site, via M frame … UX speed not impacted") — M® Checkout's
// frame on a merchant's page plays "done" from an <audio> element, not Web Audio. 1.5.38 — M's tab (the pay page) too: on the
// iPhone its "done" was lost to the same suspended context after the Face ID sheet (Alexander, 29 Oct: silent in Safari and
// Chrome), and the element plays there. Both at full level (the app's Web Audio keeps 0.7): an iPhone ignores a page's volume,
// a computer gains 3 dB. 1.5.10–1.5.11 played it through the audio
// context, which WebKit had suspended for the Face ID / Touch ID sheet and never let run again without another tap, so the
// checkout was silent (1.5.12 parked it). An element that is loaded inside the person's tap is theirs from then on (WebKit
// lifts its gesture rule for that element; m.js now delegates autoplay to the frame for Chrome and Firefox), so it can play
// seconds later, at acceptance. Nothing waits for it: the element is made and loaded in the tap (no decoding, no audio
// thread — the frame no longer builds an audio context at the tap at all), and played after Paid is painted.
let doneEl: HTMLAudioElement | null = null;
/** Inside the tap that pays: make the element and load the (cached, 24 KB) file. Cheap and synchronous. */
export function primeDoneElement(): void {
  if (doneEl) return;
  try {
    const a = new Audio();
    a.preload = "auto";
    a.src = FILES.done.url;
    a.load();
    doneEl = a;
  } catch { doneEl = null; }
}
/** At acceptance, after Paid is on screen: play it once. Best-effort; a refusal is silence, never a wait. Resolves with the
 *  element once it plays (M's tab waits for its end before closing), null when it can't. */
export function playDoneElement(): Promise<HTMLAudioElement | null> {
  window.dispatchEvent(new CustomEvent("m:sound", { detail: "done" })); // for the UI suites, as playSound
  const a = doneEl;
  if (!a) return Promise.resolve(null);
  try {
    a.volume = 1;
    a.currentTime = FILES.done.lead;
    const p = a.play();
    return p ? p.then(() => a, () => null) : Promise.resolve(a);
  } catch { return Promise.resolve(null); }
}

// 1.5.9's names, kept for Sending's slider.
export const preloadSent = (): void => preloadSound("done");
export const playSent = (): void => playDone();
